// src/components/DashboardGroups/treeUtils.ts
import type { ConversationDetail } from '@/types/conversation';

/**
 * Item shape required by react-nestable.
 * `id` must match ConversationId so the library can track items.
 */
export interface NestableItem {
  id: number;
  conversation: ConversationDetail;
  children: NestableItem[];
}

/**
 * Filter direct children of a given parent from a flat conversation list.
 */
export function getChildrenForParent(
  conversations: ConversationDetail[],
  parentId: number,
): ConversationDetail[]
{
  return conversations.filter(c => c.ParentConversationId === parentId);
}

/**
 * Build a nested tree structure from a flat list of children.
 * Handles multi-level nesting (child → grandchild → etc.) up to any depth.
 *
 * @param children - Flat list of conversations that belong under a parent
 * @returns Tree suitable for react-nestable
 */
export function buildChildTree(children: ConversationDetail[]): NestableItem[]
{
  // Index all children by their ConversationId
  const map = new Map<number, NestableItem>();
  const roots: NestableItem[] = [];

  // Create NestableItem nodes
  for (const conv of children)
  {
    map.set(conv.ConversationId, {
      id: conv.ConversationId,
      conversation: conv,
      children: [],
    });
  }

  // Build parent-child relationships within the children set
  for (const conv of children)
  {
    const node = map.get(conv.ConversationId)!;
    const parentNode = conv.ParentConversationId ? map.get(conv.ParentConversationId) : null;

    if (parentNode)
    {
      parentNode.children.push(node);
    }
    else
    {
      roots.push(node);
    }
  }

  return roots;
}

/**
 * Flatten a react-nestable tree back into a flat ConversationDetail list.
 * Assigns ParentConversationId based on tree position.
 *
 * @param items - Tree items from react-nestable
 * @param rootParentId - The ConversationId of the top-level parent card
 * @returns Flat list with updated ParentConversationId values
 */
export function flattenChildTree(
  items: NestableItem[],
  rootParentId: number,
): ConversationDetail[]
{
  const result: ConversationDetail[] = [];

  function walk(nodes: NestableItem[], parentId: number)
  {
    for (const node of nodes)
    {
      result.push({
        ...node.conversation,
        ParentConversationId: parentId,
      });
      if (node.children && node.children.length > 0)
      {
        walk(node.children, node.id);
      }
    }
  }

  walk(items, rootParentId);
  return result;
}

/**
 * Move the given conversations to the front of the list, keeping their relative order.
 * Since roots and children are rendered in array order, this makes each one first among its siblings.
 * Returns the same array reference when none of the ids are present.
 */
export function moveConversationsToTop(
  conversations: ConversationDetail[],
  ids: Set<number>,
): ConversationDetail[]
{
  if (ids.size === 0 || !conversations.some(c => ids.has(c.ConversationId)))
  {
    return conversations;
  }
  const moved = conversations.filter(c => ids.has(c.ConversationId));
  const rest = conversations.filter(c => !ids.has(c.ConversationId));
  return [...moved, ...rest];
}

/**
 * Collect ALL descendant ConversationIds under a given parent (recursive).
 * Operates on the flat list using ParentConversationId links.
 */
export function getAllDescendantIds(
  conversations: ConversationDetail[],
  parentId: number,
): Set<number>
{
  const result = new Set<number>();
  const queue = [parentId];

  while (queue.length > 0)
  {
    const currentId = queue.shift()!;
    for (const conv of conversations)
    {
      if (conv.ParentConversationId === currentId && !result.has(conv.ConversationId))
      {
        result.add(conv.ConversationId);
        queue.push(conv.ConversationId);
      }
    }
  }

  return result;
}
